import { useCallback, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { OrdersListFilter } from "services/orders/rest";
import {
    OrderDeliveryTypeDto,
    OrderPaymentTypeDto,
    OrderStatusDto,
    PriceDto,
} from "services/orders/restModels";
import { useConstants } from "shared/hooks/useConstants";
import { useLocale } from "shared/hooks/useLocale";
import { useMenuOptions } from "shared/hooks/useMenuOptions";
import { useOrders } from "shared/hooks/useOrders";
import { createCurrencyFormatter } from "shared/lib/currencyFormatter";
import { endOfDay, startOfDay } from "shared/lib/dateInput";

import { ORDERS_PAGE_SIZE } from "features/Orders/constants";
import { useOrdersFilters } from "features/Orders/hooks/useOrdersFilters";
import { rememberOrdersSearch } from "features/Orders/lib/lastOrdersSearch";
import { OrdersPaidFilter, OrdersState } from "features/Orders/types";

export const useOrdersState = (): OrdersState => {
    const locale = useLocale();
    const history = useHistory();

    const { menuOptions, loading: menusLoading, error: menusError } = useMenuOptions();
    const { constants, loading: constantsLoading, error: constantsError } = useConstants();

    // Фильтры и страница живут в query — см. useOrdersFilters.
    const { value: filters, patch, reset } = useOrdersFilters();
    const { menuId, from, to, statuses, deliveryTypes, paymentTypes, paid, page } = filters;

    // Меню в кабинете пока выбирается на самом экране (в шапку селект переедет позже) —
    // до первого выбора берём первое меню филиала.
    useEffect(() => {
        if (menuId !== null || menuOptions.length === 0) return;

        patch({ menuId: menuOptions[0].id });
    }, [menuId, menuOptions, patch]);

    const filter = useMemo<OrdersListFilter | null>(() => {
        if (menuId === null) return null;

        return {
            menuId,
            createdAfter: startOfDay(from).getTime(),
            // Конец периода доводим до 23:59:59 — иначе выбранный «по» день оказывается пустым.
            createdBefore: endOfDay(to).getTime(),
            statuses,
            deliveryTypes,
            paymentTypes,
            isPaid: paid,
        };
    }, [deliveryTypes, from, menuId, paid, paymentTypes, statuses, to]);

    const {
        orders,
        total,
        loading: ordersLoading,
        error: ordersError,
    } = useOrders(filter, (page - 1) * ORDERS_PAGE_SIZE, ORDERS_PAGE_SIZE);

    const currencies = constants?.currencies;

    const formatPrice = useCallback(
        (price: PriceDto) => {
            const currency = (currencies || []).find((item) => item.id === price.currencyId) || null;

            return createCurrencyFormatter(currency, locale).toShow(price.price);
        },
        [currencies, locale]
    );

    const setMenuId = useCallback((next: number) => patch({ menuId: next }), [patch]);

    const setFrom = useCallback((next: Date) => patch({ from: next }), [patch]);

    const setTo = useCallback((next: Date) => patch({ to: next }), [patch]);

    const setStatuses = useCallback(
        (next: OrderStatusDto[]) => patch({ statuses: next }),
        [patch]
    );

    const setDeliveryTypes = useCallback(
        (next: OrderDeliveryTypeDto[]) => patch({ deliveryTypes: next }),
        [patch]
    );

    const setPaymentTypes = useCallback(
        (next: OrderPaymentTypeDto[]) => patch({ paymentTypes: next }),
        [patch]
    );

    const setPaid = useCallback((next: OrdersPaidFilter) => patch({ paid: next }), [patch]);

    const setPage = useCallback((next: number) => patch({ page: next }), [patch]);

    const today = startOfDay(new Date()).getTime();

    // Период в признак «фильтр применён» тоже входит: пустой экран за вчерашний день без
    // этого объяснить нечем — сбрасывать вроде нечего, а заказов нет.
    const isFiltered =
        statuses.length > 0 ||
        deliveryTypes.length > 0 ||
        paymentTypes.length > 0 ||
        paid !== null ||
        startOfDay(from).getTime() !== today ||
        startOfDay(to).getTime() !== today;

    const openOrder = useCallback(
        (orderId: number) => {
            // Карточке нужно, куда возвращаться: фильтры лежат в query этого экрана.
            rememberOrdersSearch(history.location.search);
            history.push(`/stats/orders/${orderId}`);
        },
        [history]
    );

    return {
        menuOptions,
        menuId,
        setMenuId,

        orders,
        total,
        page,
        pageSize: ORDERS_PAGE_SIZE,
        setPage,

        from,
        to,
        setFrom,
        setTo,

        statuses,
        setStatuses,

        deliveryTypes,
        setDeliveryTypes,

        paymentTypes,
        setPaymentTypes,

        paid,
        setPaid,

        isFiltered,
        resetFilters: reset,

        // Константы нужны для форматирования сумм — гейтим каркас и на них.
        loading: menusLoading || constantsLoading,
        error: menusError || constantsError,
        listLoading: ordersLoading,
        listError: ordersError,

        locale,
        formatPrice,

        openOrder,
    };
};
